// ============================================
// FORMULA DIALOG COMPONENT
// Dialog for creating/editing formulas (menus)
// Extracted from POSPageV2.jsx
// ============================================

import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Package, Plus, X, Check } from 'lucide-react';

const DEFAULT_FORMULA = {
    name: '',
    description: '',
    price: '',
    steps: [
        { name: 'Entrée', choices: [], required: true },
        { name: 'Plat', choices: [], required: true },
        { name: 'Dessert', choices: [], required: false }
    ],
    meal_type: 'all'
};

// Template presets
const TEMPLATES = {
    menuMidi: {
        name: 'Menu Midi',
        meal_type: 'dejeuner',
        steps: [
            { name: 'Entrée', choices: [], required: true },
            { name: 'Plat', choices: [], required: true },
            { name: 'Dessert', choices: [], required: false }
        ]
    },
    petitDejeuner: {
        name: 'Petit Déjeuner',
        meal_type: 'petit_dejeuner',
        steps: [
            { name: 'Boisson chaude', choices: [], required: true },
            { name: 'Viennoiserie', choices: [], required: false },
            { name: 'Accompagnement', choices: [], required: false }
        ]
    },
    menuComplet: {
        name: 'Menu Complet',
        meal_type: 'dejeuner',
        steps: [
            { name: 'Entrée', choices: [], required: true },
            { name: 'Plat principal', choices: [], required: true },
            { name: 'Accompagnement', choices: [], required: false },
            { name: 'Dessert', choices: [], required: false },
            { name: 'Boisson', choices: [], required: false }
        ]
    },
    menuEnfant: {
        name: 'Menu Enfant',
        meal_type: 'all',
        steps: [
            { name: 'Plat', choices: [], required: true },
            { name: 'Boisson', choices: [], required: true },
            { name: 'Dessert', choices: [], required: false }
        ]
    },
    menuSoir: {
        name: 'Menu Soir',
        meal_type: 'diner',
        steps: [
            { name: 'Entrée', choices: [], required: false },
            { name: 'Plat principal', choices: [], required: true },
            { name: 'Dessert', choices: [], required: false }
        ]
    }
};

const FormulaDialog = ({
    open,
    onClose,
    editingFormula,
    newFormula,
    setNewFormula,
    categories,
    products,
    formulaProductFilter,
    setFormulaProductFilter,
    onAddStep,
    onRemoveStep,
    onUpdateStepName,
    onAddProductToStep,
    onRemoveProductFromStep,
    onSave,
    onUpdate,
}) => {
    const handleClose = () => {
        onClose();
    };

    const applyTemplate = (template) => {
        setNewFormula(prev => ({
            ...prev,
            name: prev.name || template.name,
            meal_type: template.meal_type,
            steps: template.steps
        }));
    };

    const getMealTypeButtonClass = (type, isSelected) => {
        const colors = {
            petit_dejeuner: isSelected ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-300 text-orange-600 hover:bg-orange-50',
            dejeuner: isSelected ? 'bg-green-500 hover:bg-green-600' : 'border-green-300 text-green-600 hover:bg-green-50',
            diner: isSelected ? 'bg-blue-500 hover:bg-blue-600' : 'border-blue-300 text-blue-600 hover:bg-blue-50',
            all: isSelected ? 'bg-indigo-500 hover:bg-indigo-600' : 'border-indigo-300 text-indigo-600 hover:bg-indigo-50'
        };
        return colors[type] || colors.all;
    };

    const filteredProducts = products
        .filter(p => p.is_available)
        .filter(p => formulaProductFilter.category === 'all' || p.category === formulaProductFilter.category)
        .filter(p => !formulaProductFilter.search || p.name.toLowerCase().includes(formulaProductFilter.search.toLowerCase()));

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-indigo-600">
                        <Package className="w-5 h-5" />
                        {editingFormula ? 'Modifier la Formule' : 'Créer une Formule / Menu'}
                    </DialogTitle>
                </DialogHeader>
                <div className="py-4 space-y-4">
                    {/* Basic info */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2 sm:col-span-1">
                            <label className="text-sm font-medium text-gray-700">Nom de la formule *</label>
                            <Input
                                placeholder="Ex: Menu du Jour"
                                value={newFormula.name}
                                onChange={(e) => setNewFormula(prev => ({ ...prev, name: e.target.value }))}
                                className="mt-1"
                            />
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                            <label className="text-sm font-medium text-gray-700">Prix (F CFA) *</label>
                            <Input
                                type="number"
                                placeholder="Ex: 5000"
                                value={newFormula.price}
                                onChange={(e) => setNewFormula(prev => ({ ...prev, price: e.target.value }))}
                                className="mt-1"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="text-sm font-medium text-gray-700">Description (optionnel)</label>
                            <Input
                                placeholder="Ex: Entrée + Plat + Dessert"
                                value={newFormula.description}
                                onChange={(e) => setNewFormula(prev => ({ ...prev, description: e.target.value }))}
                                className="mt-1"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="text-sm font-medium text-gray-700">Type de repas</label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {[
                                    { type: 'petit_dejeuner', emoji: '🌅', label: 'Petit Déjeuner' },
                                    { type: 'dejeuner', emoji: '☀️', label: 'Déjeuner' },
                                    { type: 'diner', emoji: '🌙', label: 'Dîner' },
                                    { type: 'all', emoji: '🍽️', label: 'Toute la journée' }
                                ].map(({ type, emoji, label }) => (
                                    <Button
                                        key={type}
                                        type="button"
                                        variant={newFormula.meal_type === type ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => setNewFormula(prev => ({ ...prev, meal_type: type }))}
                                        className={getMealTypeButtonClass(type, newFormula.meal_type === type)}
                                    >
                                        {emoji} {label}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>
                    
                    {/* Template buttons */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Modèles rapides :</label>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => applyTemplate(TEMPLATES.menuMidi)}
                                className="text-xs border-green-300 text-green-700 hover:bg-green-50"
                            >
                                ☀️ Menu Midi
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => applyTemplate(TEMPLATES.petitDejeuner)}
                                className="text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                            >
                                🌅 Petit Déjeuner
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => applyTemplate(TEMPLATES.menuComplet)}
                                className="text-xs border-green-300 text-green-700 hover:bg-green-50"
                            >
                                ☀️ Menu Complet
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => applyTemplate(TEMPLATES.menuEnfant)}
                                className="text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                            >
                                👶 Menu Enfant
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => applyTemplate(TEMPLATES.menuSoir)}
                                className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                            >
                                🌙 Menu Soir
                            </Button>
                        </div>
                    </div>
                    
                    {/* Steps */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="font-medium text-gray-900">Étapes de la formule ({newFormula.steps.length})</h3>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={onAddStep}
                                className="text-indigo-600 border-indigo-300"
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                Ajouter étape
                            </Button>
                        </div>
                        
                        {newFormula.steps.map((step, stepIndex) => (
                            <div key={stepIndex} className="p-3 border rounded-lg bg-gray-50">
                                <div className="flex items-center justify-between mb-2">
                                    <Input
                                        value={step.name}
                                        onChange={(e) => onUpdateStepName(stepIndex, e.target.value)}
                                        className="w-40 h-8 text-sm font-medium"
                                        placeholder="Nom de l'étape"
                                    />
                                    <div className="flex items-center gap-2">
                                        <label className="flex items-center gap-1 text-xs text-gray-600">
                                            <input
                                                type="checkbox"
                                                checked={step.required}
                                                onChange={(e) => {
                                                    const newSteps = [...newFormula.steps];
                                                    newSteps[stepIndex].required = e.target.checked;
                                                    setNewFormula(prev => ({ ...prev, steps: newSteps }));
                                                }}
                                                className="rounded"
                                            />
                                            Obligatoire
                                        </label>
                                        {newFormula.steps.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => onRemoveStep(stepIndex)}
                                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Products in this step */}
                                <div className="flex flex-wrap gap-1 mb-2">
                                    {step.choices.map((choice) => (
                                        <Badge
                                            key={choice.product_id}
                                            variant="secondary"
                                            className="flex items-center gap-1 bg-indigo-100 text-indigo-700"
                                        >
                                            {choice.product_name}
                                            <X
                                                className="w-3 h-3 cursor-pointer hover:text-red-600"
                                                onClick={() => onRemoveProductFromStep(stepIndex, choice.product_id)}
                                            />
                                        </Badge>
                                    ))}
                                    {step.choices.length === 0 && (
                                        <span className="text-xs text-gray-400 italic">Aucun produit - sélectionnez ci-dessous</span>
                                    )}
                                </div>
                                
                                {/* Category filter and search */}
                                <div className="flex gap-2 mb-2">
                                    <Select
                                        value={formulaProductFilter.category}
                                        onValueChange={(value) => setFormulaProductFilter(prev => ({ ...prev, category: value }))}
                                    >
                                        <SelectTrigger className="w-32 h-8 text-xs">
                                            <SelectValue placeholder="Catégorie" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Toutes</SelectItem>
                                            {categories.map(cat => (
                                                <SelectItem key={cat.id || cat.name} value={cat.name}>
                                                    {cat.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Input
                                        placeholder="Rechercher..."
                                        value={formulaProductFilter.search}
                                        onChange={(e) => setFormulaProductFilter(prev => ({ ...prev, search: e.target.value }))}
                                        className="h-8 text-xs flex-1"
                                    />
                                </div>
                                
                                {/* Product selection */}
                                <div className="flex gap-1 flex-wrap max-h-32 overflow-y-auto border rounded p-2 bg-white">
                                    {filteredProducts.map(product => (
                                        <Button
                                            key={product.id}
                                            type="button"
                                            variant={step.choices.find(c => c.product_id === product.id) ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => onAddProductToStep(stepIndex, product)}
                                            className={`h-7 text-xs ${step.choices.find(c => c.product_id === product.id) ? 'bg-indigo-600' : ''}`}
                                        >
                                            {step.choices.find(c => c.product_id === product.id) && <Check className="w-3 h-3 mr-1" />}
                                            {product.name}
                                        </Button>
                                    ))}
                                    {filteredProducts.length === 0 && (
                                        <span className="text-xs text-gray-400 italic p-2">Aucun produit trouvé</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Preview */}
                    {newFormula.name && newFormula.price && (
                        <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                            <h4 className="font-medium text-indigo-700 mb-1">Aperçu:</h4>
                            <p className="text-sm text-indigo-600">
                                <strong>{newFormula.name}</strong> - {parseInt(newFormula.price).toLocaleString()} F CFA
                            </p>
                            <p className="text-xs text-indigo-500">
                                {newFormula.steps.filter(s => s.choices.length > 0).map(s => s.name).join(' + ')}
                            </p>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Annuler
                    </Button>
                    <Button 
                        onClick={editingFormula ? onUpdate : onSave} 
                        className="bg-indigo-600 hover:bg-indigo-700"
                    >
                        <Check className="w-4 h-4 mr-2" />
                        {editingFormula ? 'Enregistrer' : 'Créer la formule'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default FormulaDialog;
